import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { TMentionUser } from '@onesource/schemas';
import { getMediaUrl, useGetSpacePostAuthors } from '@onesource/services';
import { useEffect, useState, type FC, type MouseEvent } from 'react';
import { MdPersonOutline, MdSearch } from 'react-icons/md';

import { FLUID_TYPE } from '../fluid-type';
import { TAG_PICKER_PAPER_SX } from '../tag-picker-panel';
import { FilterTriggerButton } from './filter-trigger-button';

const MIN_QUERY_LENGTH = 2;

type PanelProps = {
  /** Space whose post authors are searchable */
  spaceId: string;
  /** Selected mention user id */
  value?: string;
  /** null clears the filter */
  onSelect: (user: TMentionUser | null) => void;
  /** Popovers want the search focused on open; the mobile filter drawer
      doesn't — focusing would pop the keyboard the moment a section expands. */
  autoFocusSearch?: boolean;
  /** The drawer's section card already labels the panel — hide the title and
      keep only the clear action (when a member is selected). */
  hideTitle?: boolean;
};

type Props = {
  /** Space whose post authors are searchable */
  spaceId: string;
  /** Selected mention user id */
  value?: string;
  /** Display name of the selected member (kept alongside the id for the label) */
  valueName?: string;
  onChange: (user: { by: string | undefined; byName: string | undefined }) => void;
};

/**
 * The member-search panel, shared by the desktop popover and the mobile filter
 * drawer: header with a clear action, filled search input, rounded avatar rows.
 * Search state lives here — both hosts unmount the panel on close, so each
 * open starts with an empty query.
 */
export const PostedByPanel: FC<PanelProps> = ({
  spaceId,
  value,
  onSelect,
  autoFocusSearch = true,
  hideTitle = false,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the typed query (300ms) before it drives the server search.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const hasEnoughToSearch = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const { data: authors, isFetching } = useGetSpacePostAuthors(spaceId, debouncedQuery, {
    enabled: hasEnoughToSearch,
  });

  const options: TMentionUser[] = hasEnoughToSearch ? (authors ?? []) : [];
  const isSearching = hasEnoughToSearch && isFetching;

  return (
    <Box sx={{ p: 1.5 }}>
      {(!hideTitle || value) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: hideTitle ? 'flex-end' : 'space-between',
            mb: 1,
            px: 0.5,
          }}
        >
          {!hideTitle && (
            <Typography
              sx={{ fontFamily: 'Figtree, sans-serif', fontSize: FLUID_TYPE.label, fontWeight: 700, color: '#333' }}
            >
              Filter by member
            </Typography>
          )}
          {value && (
            <Button
              size="small"
              variant="contained"
              onClick={() => onSelect(null)}
              sx={{
                borderRadius: '10px',
                height: 34,
                minHeight: 34,
                textTransform: 'none',
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 600,
                px: 3,
                boxShadow: 'none',
                '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
                  background: theme => theme.palette.primary.main,
                  color: theme => theme.palette.primary.contrastText,
                  '&:hover': { background: theme => theme.palette.primary.dark, boxShadow: 'none' },
                },
                '&&.Mui-disabled': { background: '#E2E2E2 !important', color: '#9E9E9E' },
              }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}
      <Autocomplete
        size="small"
        options={options}
        loading={isSearching}
        value={null}
        inputValue={query}
        onInputChange={(_, newValue) => setQuery(newValue)}
        onChange={(_, user) => onSelect(user)}
        getOptionLabel={o => o.displayName}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={x => x} // options are already server-filtered
        noOptionsText={query.trim().length < MIN_QUERY_LENGTH ? 'Type to search members' : 'No members found'}
        forcePopupIcon={false}
        ListboxProps={{ sx: { maxHeight: 300, py: 0.5 } }}
        // Lift the results off the host surface — without a border + shadow
        // the white option list blends into the panel/drawer behind it.
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: '12px',
              border: '1px solid #f0f0f0',
              boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
            },
          },
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const isSelected = option.id === value;
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: '8px',
                mx: 0.5,
                my: '1px',
                // Match the class MUI puts on options so these paddings and
                // the selected tint win over the default option styles.
                '&.MuiAutocomplete-option': {
                  px: 1,
                  py: '6px',
                  minHeight: 'unset',
                  ...(isSelected && { bgcolor: theme => `${theme.palette.primary.main}14` }),
                },
              }}
            >
              <Avatar src={getMediaUrl(option.avatar ?? undefined)} sx={{ width: 24, height: 24 }} />
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  noWrap
                  sx={{
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: FLUID_TYPE.label,
                    fontWeight: isSelected ? 600 : 400,
                    color: '#333',
                  }}
                >
                  {option.displayName}
                </Typography>
                {option.username && (
                  <Typography noWrap sx={{ fontFamily: 'Figtree, sans-serif', fontSize: '0.72rem', color: '#8a8a8a' }}>
                    @{option.username}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            autoFocus={autoFocusSearch}
            placeholder="Search members…"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0 }}>
                  <MdSearch size={16} color="#9a9a9a" />
                </InputAdornment>
              ),
              endAdornment: isSearching ? <CircularProgress color="inherit" size={16} /> : null,
              sx: {
                fontFamily: 'Figtree, sans-serif',
                fontSize: FLUID_TYPE.label,
                bgcolor: '#f6f6f6',
                transition: 'background-color 0.15s ease',
                // Theme forces OutlinedInput to 20px; match the selector to override.
                '&.MuiOutlinedInput-root': { borderRadius: '10px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
                '&.Mui-focused': { bgcolor: 'common.white' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

/**
 * "Posted by" filter — member name search backed by the mention users
 * endpoint. Selecting a member filters the feed to their posts.
 */
export const PostedByFilter: FC<Props> = ({ spaceId, value, valueName, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSelect = (user: TMentionUser | null) => {
    onChange({ by: user?.id, byName: user?.displayName });
    setAnchorEl(null);
  };

  return (
    <>
      <FilterTriggerButton
        label="Posted by"
        icon={<MdPersonOutline size={15} />}
        value={value ? valueName || 'Member' : undefined}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
      />
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        disableAutoFocus
        disableEnforceFocus
        // Don't return focus to the trigger button on close — selecting a member
        // closes the popover, and that programmatic refocus makes the browser
        // select the button's (just-updated) label text, which flashes as a grey
        // highlight. Consistent with the relaxed focus model above.
        disableRestoreFocus
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: TAG_PICKER_PAPER_SX } }}
      >
        <PostedByPanel spaceId={spaceId} value={value} onSelect={handleSelect} />
      </Popover>
    </>
  );
};
